import "./routeSummary.css";

import { ArrowRightOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { Image, Popover, Typography } from "antd";

import { checkPointsDTO } from "../../../../shared/dtos/checkPointsDTO";
import {
  DestinationIcon,
  OriginIcon,
} from "../../../../shared/constants/Images";
import { IconDarkMode } from "../../../../shared/constants/Resources";
import { sortDestinations } from "../../../../utils/helpers/sortDestinations";
import { shortenAddress } from "./shortenAddress";

const { Text } = Typography;

interface RouteSummaryProps {
  origin: string;
  destination: string;
  destinations?: checkPointsDTO[] | null;
  fallbackDestination?: string | null;
  isTextDarkMode: boolean;
}

const RouteSummary = ({
  origin,
  destination,
  destinations,
  fallbackDestination,
  isTextDarkMode,
}: RouteSummaryProps) => {
  const iconFilter = isTextDarkMode ? IconDarkMode : "";

  const destinationStops = destinations?.length
    ? sortDestinations(destinations)
        .map((stop) => stop.address?.trim())
        .filter((address): address is string => Boolean(address))
    : [fallbackDestination?.trim() || destination].filter(Boolean);

  const finalDestinationIndex = destinationStops.length - 1;

  const getDestinationLabel = (index: number) => {
    if (destinationStops.length === 1) {
      return "Destino final";
    }
    if (index === finalDestinationIndex) {
      return `Destino final`;
    }
    return `Parada ${index + 1}`;
  };

  const popoverContent = (
    <div className="route-summary-popover">
      <div className="route-summary-popover-block">
        <Text type="secondary" className="route-summary-popover-label">
          Origen
        </Text>
        <Text className="route-summary-popover-address">{origin}</Text>
      </div>
      {destinationStops.map((address, index) => (
        <div
          key={`${index}-${address}`}
          className="route-summary-popover-block"
        >
          <Text type="secondary" className="route-summary-popover-label">
            {getDestinationLabel(index)}
          </Text>
          <Text className="route-summary-popover-address">{address}</Text>
        </div>
      ))}
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      title="Ruta del viaje"
      trigger="click"
      placement="rightTop"
      overlayClassName="route-summary-popover-overlay"
    >
      <button
        type="button"
        className="route-summary"
        onClick={(event) => event.stopPropagation()}
        aria-label="Ver direcciones completas"
      >
        <span className="route-summary-point">
          <Image
            preview={false}
            src={OriginIcon}
            alt="Origen"
            width={18}
            style={{ filter: iconFilter }}
          />
          <Text strong ellipsis title={origin}>
            {shortenAddress(origin)}
          </Text>
        </span>
        <ArrowRightOutlined className="route-summary-arrow" aria-hidden />
        <span className="route-summary-point route-summary-point--final">
          <Image
            preview={false}
            src={DestinationIcon}
            alt="Destino final"
            width={18}
            style={{ filter: iconFilter }}
          />
          <span className="route-summary-point-text">
            <span className="route-summary-inline-label">Destino final</span>
            <Text strong ellipsis title={destination}>
              {shortenAddress(destination)}
            </Text>
          </span>
        </span>
        <EnvironmentOutlined
          className="route-summary-expand-hint"
          title="Ver direcciones completas"
        />
      </button>
    </Popover>
  );
};

export default RouteSummary;
